<script>
  import { onMount } from "svelte";
  import * as THREE from "three";

  let containerEl;
  let canvasEl;

  export let onScrollHint = () => {};

  onMount(() => {
    /* ─── Renderer ─── */
    const renderer = new THREE.WebGLRenderer({
      canvas: canvasEl,
      antialias: true,
      alpha: false,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 1);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    /* ─── Text Texture via OffscreenCanvas ─── */
    const textCanvas = document.createElement("canvas");
    const textCtx = textCanvas.getContext("2d");
    let textTexture = null;

    function renderTextTexture(w, h) {
      textCanvas.width = w;
      textCanvas.height = h;
      textCtx.clearRect(0, 0, w, h);

      const fontSize = Math.max(48, Math.min(w * 0.13, 180));
      textCtx.fillStyle = "#ffffff";
      textCtx.textAlign = "center";
      textCtx.textBaseline = "middle";
      textCtx.font = `300 ${fontSize}px "Cormorant Garamond", "Georgia", serif`;
      textCtx.fillText("WebLab", w / 2, h / 2);

      // Subtle subtitle
      const subSize = Math.max(11, fontSize * 0.12);
      textCtx.font = `300 ${subSize}px "Inter", "Helvetica Neue", sans-serif`;
      textCtx.fillStyle = "rgba(255, 255, 255, 0.35)";
      textCtx.letterSpacing = "0.35em";
      textCtx.fillText("L E A R N I N G   A R C H I V E", w / 2, h / 2 + fontSize * 0.52);

      if (textTexture) textTexture.dispose();
      textTexture = new THREE.CanvasTexture(textCanvas);
      textTexture.minFilter = THREE.LinearFilter;
      textTexture.magFilter = THREE.LinearFilter;
      return textTexture;
    }

    /* ─── Shader Material ─── */
    const vertexShader = `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position, 1.0);
      }
    `;

    const fragmentShader = `
      precision highp float;

      uniform sampler2D u_texture;
      uniform vec2 u_resolution;
      uniform vec2 u_mouse;
      uniform float u_time;
      uniform float u_clickTime;
      uniform vec2 u_clickPos;
      uniform float u_clickStrength;

      varying vec2 vUv;

      /* Simplex-like noise for organic feel */
      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
      }

      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        float a = hash(i);
        float b = hash(i + vec2(1.0, 0.0));
        float c = hash(i + vec2(0.0, 1.0));
        float d = hash(i + vec2(1.0, 1.0));
        return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
      }

      void main() {
        vec2 uv = vUv;
        float aspect = u_resolution.x / u_resolution.y;

        /* ── Mouse proximity ripple ── */
        vec2 mouse = u_mouse;
        vec2 diff = uv - mouse;
        diff.x *= aspect;
        float dist = length(diff);

        // Soft ripple radius
        float rippleRadius = 0.28;
        float rippleInfluence = smoothstep(rippleRadius, 0.0, dist);

        // Multi-frequency water ripple
        float wave1 = sin(dist * 28.0 - u_time * 2.8) * 0.5 + 0.5;
        float wave2 = sin(dist * 42.0 - u_time * 3.6) * 0.5 + 0.5;
        float wave3 = sin(dist * 18.0 - u_time * 1.9) * 0.5 + 0.5;
        float combinedWave = wave1 * 0.5 + wave2 * 0.3 + wave3 * 0.2;

        float displacement = rippleInfluence * combinedWave * 0.025;

        // Direction from mouse
        vec2 rippleDir = dist > 0.001 ? normalize(diff) : vec2(0.0);
        vec2 rippleOffset = rippleDir * displacement;

        /* ── Click wave ── */
        float clickElapsed = u_time - u_clickTime;
        float clickWaveRadius = clickElapsed * 0.55;
        vec2 clickDiff = uv - u_clickPos;
        clickDiff.x *= aspect;
        float clickDist = length(clickDiff);

        float clickWaveWidth = 0.12;
        float clickWaveFront = smoothstep(clickWaveRadius - clickWaveWidth, clickWaveRadius, clickDist)
                             * smoothstep(clickWaveRadius + clickWaveWidth, clickWaveRadius, clickDist);
        float clickFade = exp(-clickElapsed * 1.2) * u_clickStrength;
        float clickWave = sin(clickDist * 50.0 - clickElapsed * 8.0) * clickWaveFront * clickFade * 0.04;

        vec2 clickDir = clickDist > 0.001 ? normalize(clickDiff) : vec2(0.0);
        vec2 clickOffset = clickDir * clickWave;

        /* ── Ambient organic movement ── */
        float ambientX = noise(uv * 3.0 + u_time * 0.15) * 0.003;
        float ambientY = noise(uv * 3.0 + u_time * 0.15 + 100.0) * 0.003;
        vec2 ambientOffset = vec2(ambientX, ambientY);

        /* ── Final UV ── */
        vec2 finalUv = uv + rippleOffset + clickOffset + ambientOffset;

        vec4 texColor = texture2D(u_texture, finalUv);

        /* ── Subtle glow around mouse ── */
        float glow = rippleInfluence * 0.04;

        /* ── Click ring glow ── */
        float ringGlow = clickWaveFront * clickFade * 0.12;

        /* ── Vignette ── */
        vec2 vigUv = uv * 2.0 - 1.0;
        float vignette = 1.0 - dot(vigUv * 0.4, vigUv * 0.4);

        vec3 finalColor = texColor.rgb + vec3(glow) + vec3(ringGlow * 0.6, ringGlow * 0.7, ringGlow);

        gl_FragColor = vec4(finalColor * vignette, 1.0);
      }
    `;

    const uniforms = {
      u_texture: { value: null },
      u_resolution: { value: new THREE.Vector2() },
      u_mouse: { value: new THREE.Vector2(0.5, 0.5) },
      u_time: { value: 0 },
      u_clickTime: { value: -10.0 },
      u_clickPos: { value: new THREE.Vector2(0.5, 0.5) },
      u_clickStrength: { value: 0 },
    };

    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms,
    });

    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    /* ─── Sizing ─── */
    function resize() {
      const w = containerEl.clientWidth;
      const h = containerEl.clientHeight;
      renderer.setSize(w, h);
      uniforms.u_resolution.value.set(w, h);
      uniforms.u_texture.value = renderTextTexture(w * Math.min(window.devicePixelRatio, 2), h * Math.min(window.devicePixelRatio, 2));
    }

    resize();
    window.addEventListener("resize", resize);

    /* ─── Mouse tracking (smooth lerp) ─── */
    let targetMouse = { x: 0.5, y: 0.5 };
    let currentMouse = { x: 0.5, y: 0.5 };

    function onMouseMove(e) {
      const rect = canvasEl.getBoundingClientRect();
      targetMouse.x = (e.clientX - rect.left) / rect.width;
      targetMouse.y = 1.0 - (e.clientY - rect.top) / rect.height;
    }

    function onTouchMove(e) {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        const rect = canvasEl.getBoundingClientRect();
        targetMouse.x = (touch.clientX - rect.left) / rect.width;
        targetMouse.y = 1.0 - (touch.clientY - rect.top) / rect.height;
      }
    }

    function onClick(e) {
      const rect = canvasEl.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1.0 - (e.clientY - rect.top) / rect.height;
      uniforms.u_clickTime.value = uniforms.u_time.value;
      uniforms.u_clickPos.value.set(x, y);
      uniforms.u_clickStrength.value = 1.0;
    }

    function onTouchStart(e) {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        const rect = canvasEl.getBoundingClientRect();
        const x = (touch.clientX - rect.left) / rect.width;
        const y = 1.0 - (touch.clientY - rect.top) / rect.height;
        uniforms.u_clickTime.value = uniforms.u_time.value;
        uniforms.u_clickPos.value.set(x, y);
        uniforms.u_clickStrength.value = 1.0;
      }
    }

    canvasEl.addEventListener("mousemove", onMouseMove);
    canvasEl.addEventListener("touchmove", onTouchMove, { passive: true });
    canvasEl.addEventListener("click", onClick);
    canvasEl.addEventListener("touchstart", onTouchStart, { passive: true });

    /* ─── Animation Loop ─── */
    const clock = new THREE.Clock();
    let animFrame;

    function animate() {
      animFrame = requestAnimationFrame(animate);

      uniforms.u_time.value = clock.getElapsedTime();

      // Smooth mouse lerp for fluid feel
      currentMouse.x += (targetMouse.x - currentMouse.x) * 0.06;
      currentMouse.y += (targetMouse.y - currentMouse.y) * 0.06;
      uniforms.u_mouse.value.set(currentMouse.x, currentMouse.y);

      renderer.render(scene, camera);
    }

    animate();

    /* ─── Cleanup ─── */
    return () => {
      cancelAnimationFrame(animFrame);
      canvasEl.removeEventListener("mousemove", onMouseMove);
      canvasEl.removeEventListener("touchmove", onTouchMove);
      canvasEl.removeEventListener("click", onClick);
      canvasEl.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("resize", resize);
      renderer.dispose();
      geometry.dispose();
      material.dispose();
      if (textTexture) textTexture.dispose();
    };
  });
</script>

<section class="hero-section" id="hero-section">
  <div class="hero-container" bind:this={containerEl}>
    <canvas bind:this={canvasEl} class="hero-canvas" aria-label="WebLab interactive ripple animation"></canvas>
  </div>

  <button class="scroll-hint" on:click={onScrollHint} aria-label="Scroll down to blog">
    <span class="scroll-hint-line"></span>
    <span class="scroll-hint-text">Scroll</span>
  </button>
</section>

<style>
  .hero-section {
    width: 100%;
    height: max(400px, 100vh);
    position: relative;
    background: #000000;
    overflow: hidden;

  }

  .hero-container {
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
  }

  .hero-canvas {
    display: block;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }

  /* ── Scroll Hint ── */
  .scroll-hint {
    position: absolute;
    bottom: var(--spacing-xl);
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-sm);
    background: none;
    border: none;
    cursor: pointer;
    z-index: 10;
    padding: 10px;
  }

  .scroll-hint-line {
    display: block;
    width: 1px;
    height: 40px;
    background: linear-gradient(to bottom, transparent, var(--outline));
    animation: scrollPulse 2s ease-in-out infinite;
  }

  .scroll-hint-text {
    font-family: "Inter", sans-serif;
    font-size: 0.68rem;
    font-weight: 400;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: var(--muted);
    animation: scrollFade 2s ease-in-out infinite;
  }

  @keyframes scrollPulse {
    0%, 100% { opacity: 0.3; transform: scaleY(0.7); }
    50% { opacity: 1; transform: scaleY(1); }
  }

  @keyframes scrollFade {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 0.6; }
  }

  @media (max-width: 900px) {
    .scroll-hint {
      bottom: 28px;
    }
  }
</style>
